import React, { useCallback } from 'react';
import { AlertTriangle, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import Modal from './Modal';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const DIALOG_TYPES = {
    DELETE: {
        icon: AlertTriangle,
        iconColor: 'text-red-500 dark:text-red-400',
        buttonColor: 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700',
        titleKey: 'Delete'
    },
    EDIT: {
        icon: AlertCircle,
        iconColor: 'text-orange-500 dark:text-orange-400',
        buttonColor: 'bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700',
        titleKey: 'Edit'
    },
    SUCCESS: {
        icon: CheckCircle,
        iconColor: 'text-green-500 dark:text-green-400',
        buttonColor: 'bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700',
        titleKey: 'Success'
    },
    WARNING: {
        icon: AlertCircle,
        iconColor: 'text-orange-500 dark:text-orange-400',
        buttonColor: 'bg-[#1EBDEF] hover:bg-[#1EBDEF]/80 dark:bg-[#1EBDEF] dark:hover:bg-[#1EBDEF]/80',
        titleKey: 'Warning'
    }
};

const ConfirmationDialog = ({ isOpen, onClose, onConfirm, type = 'DELETE', title, message, confirmLabel, cancelLabel, isLoading = false }) => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`deleteConfirmation:${key}`), [t]);
    const dialogConfig = DIALOG_TYPES[type];
    const Icon = dialogConfig.icon;

    // Use translations for default values
    const defaultTitle = t(dialogConfig.titleKey);
    const defaultMessage = tr('message');
    const defaultConfirmLabel = tr('confirm');
    const defaultCancelLabel = tr('cancel');

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            modalWidth="xs" // Using smallest width
            title={title || defaultTitle}
        >
            <div className="flex flex-col items-center text-center">
                {/* Icon and Message Container */}
                <div className="flex items-center space-x-3 mb-4">
                    <Icon className={clsx('w-12 h-12', dialogConfig.iconColor)} />
                    <p className="text-sm text-gray-600 dark:text-gray-300">{message || defaultMessage}</p>
                </div>

                {/* Buttons */}
                <div className="flex gap-2 w-full">
                    <button
                        type="button"
                        onClick={onClose}
                        className={clsx(
                            'flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                            'text-gray-700 dark:text-gray-200',
                            'bg-gray-100 dark:bg-gray-800',
                            'hover:bg-gray-200 dark:hover:bg-gray-700',
                            'focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600'
                        )}
                    >
                        {cancelLabel || defaultCancelLabel}
                    </button>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => {
                            onConfirm();
                            if (type === 'SUCCESS') {
                                onClose();
                            }
                        }}
                        className={clsx('flex-1 px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors', dialogConfig.buttonColor, 'focus:ring-2 focus:ring-offset-2')}
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center">
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                <span>...</span>
                            </div>
                        ) : (confirmLabel || defaultConfirmLabel)}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

ConfirmationDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['DELETE', 'EDIT', 'SUCCESS', 'WARNING']),
    title: PropTypes.string,
    message: PropTypes.string,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    isLoading: PropTypes.bool
};

export default ConfirmationDialog;
